import type { Facility } from '@reliance/types/models/Facility';
import type { HousingUnit } from '@reliance/types/models/HousingUnit';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import api from '../common/api';
import { useStations } from '../hooks/Stations';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import StationTable from '../station/Table';
import { useAlerts } from '../alert/AlertContext';

type FacilityStationsTabProps = {
  facility: Facility;
};

type NewStationForm = {
  name: string;
  type: string;
  userId: string;
  password: string;
  status: 'Active' | 'Inactive';
  domain: string;
  tollAllow: 'True' | 'False';
  userContext: string;
  housingUnit: HousingUnit | null;
};

const defaultForm = (): NewStationForm => ({
  name: '',
  type: 'Inmate',
  userId: '',
  password: '',
  status: 'Active',
  domain: '',
  tollAllow: 'False',
  userContext: '',
  housingUnit: null,
});

const FacilityStationsTab: React.FC<FacilityStationsTabProps> = ({ facility }) => {
  const { result: stations, loading, execute } = useStations({ facilityId: facility.id });
  const [housingUnits, setHousingUnits] = useState<HousingUnit[]>([]);
  const [form, setForm] = useState<NewStationForm>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [pushing, setPushing] = useState(false);
  const alerts = useAlerts();

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.get<{ data: HousingUnit[] }>('/housing-unit', {
          params: { page: 1, perPage: 1000, facilityId: facility.id, orderBy: '-id' },
        });
        setHousingUnits(res.data.data ?? []);
      } catch {
        alerts.error('Failed to load housing units', true);
      }
    })();
  }, [facility.id]);

  const handleAdd = useCallback(async () => {
    setSubmitting(true);
    try {
      await api.post('/station', {
        name: form.name,
        domain: form.domain,
        userContext: form.userContext,
        userId: form.userId,
        password: form.password ?? '',
        type: form.type,
        status: form.status,
        facilityId: facility.id,
        tollAllow: form.tollAllow === 'True',
        housingUnitId: form.type === 'Inmate' && form.housingUnit != null ? form.housingUnit.id : null,
        pairedDevice: null,
        deviceId: null,
        defaultExtension: null,
      });
      alerts.success('New station added successfully', true);
      setForm(defaultForm());
      await execute();
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } }).response?.status;
      if (status === 503) {
        alerts.warning('New station was added but site cache could not be cleared', true);
        await execute();
      } else {
        alerts.error('An error occurred while adding new station', true);
      }
    } finally {
      setSubmitting(false);
    }
  }, [facility.id, form, execute]);

  useNotifyTabLoading('stations', loading || submitting || pushing);

  const pushStationConfig = useCallback(async () => {
    setPushing(true);
    try {
      await api.put(`/mobile-device/push-station-config?facilityId=${facility.id}`);
      alerts.success('The config will be sent to the devices shortly');
    } catch {
      alerts.error('An error occurred when pushing station configs');
    } finally {
      setPushing(false);
    }
  }, [facility.id]);

  return (
    <div className="jarviswidget jarviswidget-color-blueDark">
      <div className="content">
        <div className="widget-body">
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              void handleAdd();
            }}>
            <div className="form-horizontal card">
              <div className="card-body">
                <Row>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        required
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Type</Form.Label>
                      <Form.Select
                        value={form.type}
                        onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                        <option>Officer</option>
                        <option>Inmate</option>
                        <option>Visitor</option>
                        <option>Phone</option>
                        <option>Audio Visit</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>User ID</Form.Label>
                      <Form.Control
                        required={form.type !== 'Phone'}
                        value={form.userId}
                        onChange={(e) => setForm((f) => ({ ...f, userId: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="text"
                        required={form.type !== 'Phone'}
                        value={form.password}
                        onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        value={form.status}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, status: e.target.value as 'Active' | 'Inactive' }))
                        }>
                        <option>Active</option>
                        <option>Inactive</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Domain</Form.Label>
                      <Form.Control
                        required={form.type !== 'Phone'}
                        value={form.domain}
                        onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Toll Allow</Form.Label>
                      <Form.Select
                        value={form.tollAllow}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, tollAllow: e.target.value as 'True' | 'False' }))
                        }>
                        <option>True</option>
                        <option>False</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>User Context</Form.Label>
                      <Form.Control
                        required={form.type !== 'Phone'}
                        value={form.userContext}
                        onChange={(e) => setForm((f) => ({ ...f, userContext: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  {form.type === 'Inmate' && (
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>Housing Unit</Form.Label>
                        <Form.Select
                          value={form.housingUnit?.id ?? ''}
                          onChange={(e) => {
                            const id = parseInt(e.target.value, 10);
                            const hu = housingUnits.find((h) => h.id === id) ?? null;
                            setForm((f) => ({ ...f, housingUnit: hu }));
                          }}>
                          <option value="">(none)</option>
                          {housingUnits.map((hu) => (
                            <option key={hu.id} value={hu.id}>
                              {hu.name}
                            </option>
                          ))}
                        </Form.Select>
                        {form.housingUnit && (
                          <div>
                            <Link to={`/housing-units/view/${form.housingUnit.id}`}>
                              <i className="fas fa-cubes" /> {form.housingUnit.name}
                            </Link>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              </div>
              <div className="card-footer">
                <Row>
                  <div className="col-md-12">
                    <Button type="submit" className="pull-right" disabled={submitting}>
                      Add
                    </Button>
                  </div>
                </Row>
              </div>
            </div>
          </Form>
        </div>
        <div className="row">
          <div className="col-md-12">
            <Button className="btn btn-secondary pull-right mb-2" disabled={pushing} onClick={() => void pushStationConfig()}>
              Push Config
            </Button>
          </div>
        </div>
        <StationTable rows={stations ?? []} loading={loading} />
      </div>
    </div>
  );
};

export default FacilityStationsTab;
